import React, { useState, useEffect, useRef } from 'react';
import { API_URL } from '../config/constants';

export interface Product {
  _id: string;
  productCode: string;
  prototypeCode?: string;
  modelName: string;
  category: string;
  subCategory: string;
  description: string;
  imageUrl?: string;
  isPrototype: boolean;
  createdAt: string;
}

interface ProductSelectorProps {
  selectedProduct: Product | null;
  onProductSelect: (product: Product) => void;
  showPrototypes?: boolean;
}

const ProductSelector: React.FC<ProductSelectorProps> = ({ 
  selectedProduct, 
  onProductSelect,
  showPrototypes = false 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  
  const selectorRef = useRef<HTMLDivElement>(null);

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${API_URL}/products?isPrototype=${showPrototypes}`);
      if (!response.ok) throw new Error('Ürünler yüklenemedi');
      const data = await response.json();
      setProducts(data);
      setFilteredProducts(data);
    } catch (error) {
      console.error('Ürünler yüklenirken hata oluştu:', error);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, [showPrototypes]);

  useEffect(() => {
    const filtered = products.filter(product =>
      product.modelName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (product.prototypeCode || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.category.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredProducts(filtered);
  }, [searchTerm, products]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectorRef.current && !selectorRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={selectorRef}>
      {/* Seçici Butonu */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white text-left flex items-center space-x-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      >
        {selectedProduct && selectedProduct.imageUrl && (
          <img 
            src={selectedProduct.imageUrl} 
            alt={selectedProduct.modelName} 
            className="w-10 h-10 object-cover rounded"
          />
        )}
        <span className="text-gray-700 flex-1">
          {selectedProduct 
            ? `${selectedProduct.modelName} ${selectedProduct.isPrototype ? `(${selectedProduct.prototypeCode})` : ''}`
            : showPrototypes ? 'Prototip Seçin' : 'Ürün Seçin'}
        </span>
        <svg className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown Menü */}
      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
          <div className="p-2 border-b">
            <div className="relative">
              <input
                type="text"
                placeholder={showPrototypes ? "Prototip Ara..." : "Ürün Ara..."}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <svg className="absolute left-2 top-2.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
          
          <div className="max-h-60 overflow-y-auto">
            {filteredProducts.map(product => (
              <button
                key={product._id}
                type="button"
                onClick={() => {
                  onProductSelect(product);
                  setIsOpen(false);
                }}
                className="w-full px-4 py-2 text-left hover:bg-gray-100 focus:outline-none focus:bg-gray-100 flex items-center space-x-3"
              >
                {product.imageUrl && (
                  <img 
                    src={API_URL + product.imageUrl} 
                    alt={API_URL + product.modelName} 
                    className="w-12 h-12 object-cover rounded"
                  />
                )}
                <div>
                  <div className="font-medium">{product.modelName}</div>
                  {product.isPrototype && product.prototypeCode && (
                    <div className="text-sm text-blue-600 font-medium">{product.prototypeCode}</div>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductSelector; 